<template>
    <div class="assignment-table-wrap">
        <table style="width: 100%">
            <tr>
                <td>
                    <o-input v-model="filter" icon="filter" placeholder="Filter results" expanded clearable class="mb-4"></o-input>
                    <o-switch v-model="showHidden">Show hidden columns</o-switch>
                    <div v-if="groupedMode" class="group-controls mt-2 mb-2">
                        <o-button size="small" @click="ExpandAllGroups">Expand all</o-button>
                        <o-button size="small" @click="CollapseAllGroups">Collapse all</o-button>
                    </div>
                </td>
                <td class="has-text-right">
                    <div>{{ $filters.AddCommas(assignmentsFiltered.length) }} Assignments</div>
                    <div>
                        {{
                            $filters.Currency(
                                $store.getters.user.Currency,
                                assignmentsFiltered.reduce((acc, x) => (acc += x.totalBillings), 0)
                            )
                        }}
                        Total billings
                    </div>
                </td>
            </tr>
        </table>

        <div v-if="!groupedMode">
            <o-table
                :data="assignmentsFiltered"
                :paginated="assignmentsFiltered.length > openColumns"
                :pagination-simple="true"
                :per-page="openColumns"
                :defaultSort="[sort, direction]"
                :sticky-header="true"
                hoverable>
                <template #empty>
                    <h3 class="py-6">No assignments match the filters</h3>
                </template>
                <o-table-column field="Id" label="Job Title" numeric sortable v-slot="props" class="default">
                    <router-link v-if="props.row.Id" target="_blank" :to="{ name: '_assignmentDetail', params: { Id: props.row.Id } }">
                        {{ props.row.jobTitle }}
                    </router-link>
                    <span v-else>{{ props.row.jobTitle }}</span>
                </o-table-column>
                <o-table-column field="company" label="Client" numeric sortable v-slot="props">
                    <router-link v-if="props.row.companyId" target="_blank" :to="{ name: '_companyDetail', params: { Id: props.row.companyId } }">
                        {{ props.row.company }}
                        <span v-if="props.row.isKeyAccount"><i class="fal fa-key theme-key"></i></span>
                        <span v-if="hasActivePPA(props.row)"><i class="fal fa-file-alt theme-ppa"></i></span>
                    </router-link>
                    <span v-else>
                        {{ props.row.company }}
                        <span v-if="props.row.isKeyAccount"><i class="fal fa-key theme-key"></i></span>
                        <span v-if="hasActivePPA(props.row)"><i class="fal fa-file-alt theme-ppa"></i></span>
                    </span>
                </o-table-column>
                <o-table-column field="displayId" label="Assignment #" sortable v-slot="props" v-if="showHidden">
                    <router-link v-if="props.row.Id" target="_blank" :to="{ name: '_assignmentDetail', params: { Id: props.row.Id } }">
                        {{ props.row.displayId }}
                    </router-link>
                    <span v-else>{{ props.row.displayId }}</span>
                </o-table-column>
                <o-table-column field="industry" label="Industry" v-slot="props" v-if="showHidden">
                    {{ props.row.industry }}
                </o-table-column>
                <o-table-column field="function" label="Job Function" v-slot="props" v-if="showHidden">
                    {{ props.row.function }}
                </o-table-column>
                <o-table-column field="users" label="License" v-slot="props" v-if="showHidden">
                    <div v-for="license in Licenses(props.row)" :key="license">
                        {{ license }}
                    </div>
                </o-table-column>
                <o-table-column field="searchType" label="Type" v-slot="props">
                    <div v-if="props.row.searchType == 'BLM' || props.row.searchType == 'Consulting'">
                        {{ props.row.assignmentType }}
                    </div>
                    <div v-else>
                        {{ $filters.SearchType(props.row.searchType) }}
                    </div>
                </o-table-column>
                <o-table-column field="users" label="Partners" v-slot="props">
                    <div class="partners">
                        <div
                            class="partner-line"
                            v-for="partner in PartnerEntries(props.row)"
                            :key="partner.key">
                            <router-link
                                v-if="partner.id"
                                target="_blank"
                                rel="noopener noreferrer"
                                :to="{ name: '_partnerProfile', params: { Id: partner.id } }">
                                {{ partner.name }}
                            </router-link>
                            <span v-else>{{ partner.name }}</span>
                            <span> - {{ partner.percent }}</span>
                        </div>
                    </div>
                </o-table-column>
                <o-table-column field="users" label="Role" v-slot="props" style="white-space: nowrap" v-if="width > 58">
                    <div v-for="user in ParseUsers(props.row)" :key="(user.ID || user.Id) + '-role'">
                        <span style="white-space: nowrap">{{ $filters.UserRole(user) }}</span>
                    </div>
                </o-table-column>
                <o-table-column v-if="reportMode || width > 66" field="users" :label="reportMode ? 'Offices' : 'Location'" v-slot="props">
                    <div v-for="office in Offices(props.row)" :key="office">
                        {{ office }}
                    </div>
                </o-table-column>
                <o-table-column field="status" label="Status" sortable v-slot="props">
                    {{ $filters.AssignmentStatus(props.row.status) }}
                </o-table-column>
                <o-table-column field="dateTaken" label="Taken" sortable v-slot="props">
                    {{ moment(props.row.dateTaken).format("MM/DD/YYYY") }}
                </o-table-column>
                <o-table-column
                    field="totalBillings"
                    position="right"
                    :label="reportMode ? 'Billings' : $store.getters.user.Currency"
                    sortable
                    v-slot="props"
                    v-if="reportMode || width > 42">
                    {{ $filters.Currency($store.getters.user.Currency, RowBillings(props.row)) }}
                </o-table-column>
            </o-table>
        </div>
        <div v-else class="grouped-assignment-results">
            <div class="grouped-assignment-scroll">
            <table class="table is-fullwidth is-hoverable assignment-group-table">
                <thead>
                    <tr>
                        <th @click="ToggleGroupedSort('jobTitle')" class="is-sortable">
                            <span class="sortable-label">Job Title <span class="sort-indicator" :class="SortState('jobTitle')"></span></span>
                        </th>
                        <th @click="ToggleGroupedSort('company')" class="is-sortable">
                            <span class="sortable-label">Client <span class="sort-indicator" :class="SortState('company')"></span></span>
                        </th>
                        <th v-if="showHidden" @click="ToggleGroupedSort('displayId')" class="is-sortable">
                            <span class="sortable-label">Assignment # <span class="sort-indicator" :class="SortState('displayId')"></span></span>
                        </th>
                        <th v-if="showHidden" @click="ToggleGroupedSort('industry')" class="is-sortable">
                            <span class="sortable-label">Industry <span class="sort-indicator" :class="SortState('industry')"></span></span>
                        </th>
                        <th v-if="showHidden" @click="ToggleGroupedSort('function')" class="is-sortable">
                            <span class="sortable-label">Job Function <span class="sort-indicator" :class="SortState('function')"></span></span>
                        </th>
                        <th v-if="showHidden" @click="ToggleGroupedSort('license')" class="is-sortable">
                            <span class="sortable-label">License <span class="sort-indicator" :class="SortState('license')"></span></span>
                        </th>
                        <th @click="ToggleGroupedSort('searchType')" class="is-sortable">
                            <span class="sortable-label">Type <span class="sort-indicator" :class="SortState('searchType')"></span></span>
                        </th>
                        <th @click="ToggleGroupedSort('partners')" class="is-sortable">
                            <span class="sortable-label">Partners <span class="sort-indicator" :class="SortState('partners')"></span></span>
                        </th>
                        <th v-if="width > 58" @click="ToggleGroupedSort('role')" class="is-sortable">
                            <span class="sortable-label">Role <span class="sort-indicator" :class="SortState('role')"></span></span>
                        </th>
                        <th @click="ToggleGroupedSort('office')" class="is-sortable">
                            <span class="sortable-label">
                                {{ reportMode ? "Offices" : "Location" }} <span class="sort-indicator" :class="SortState('office')"></span>
                            </span>
                        </th>
                        <th @click="ToggleGroupedSort('status')" class="is-sortable">
                            <span class="sortable-label">Status <span class="sort-indicator" :class="SortState('status')"></span></span>
                        </th>
                        <th @click="ToggleGroupedSort('dateTaken')" class="is-sortable">
                            <span class="sortable-label">Taken <span class="sort-indicator" :class="SortState('dateTaken')"></span></span>
                        </th>
                        <th class="has-text-right is-sortable" @click="ToggleGroupedSort('billings')">
                            <span class="sortable-label">
                                {{ reportMode ? "Billings" : $store.getters.user.Currency }} <span class="sort-indicator" :class="SortState('billings')"></span>
                            </span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="group in groupedAssignments" :key="group.key">
                        <tr class="assignment-group-header-row">
                            <td :colspan="groupHeaderColspan">
                                <div class="assignment-group-title">
                                    <button type="button" class="assignment-group-toggle" @click="ToggleGroup(group.key)">
                                        <span class="assignment-group-chevron" :class="{ collapsed: GroupCollapsed(group.key) }">▾</span>
                                        <span>{{ group.key }}</span>
                                    </button>
                                    <div class="assignment-group-summary">
                                        <span class="assignment-group-count">
                                            {{ $filters.AddCommas(group.rows.length) }} {{ group.rows.length === 1 ? "Job" : "Jobs" }}
                                        </span>
                                        <span>
                                            {{
                                                $filters.Currency(
                                                    $store.getters.user.Currency,
                                                    group.rows.reduce((acc, row) => acc + RowBillings(row), 0)
                                                )
                                            }}
                                        </span>
                                    </div>
                                </div>
                            </td>
                        </tr>
                        <tr
                            v-for="(row, rowIndex) in group.rows"
                            :key="group.key + '-' + row.Id + '-' + (row.__groupUser?.ID || row.__groupUser?.Id || '') + '-' + rowIndex"
                            v-show="!GroupCollapsed(group.key)">
                            <td>
                                <router-link v-if="row.Id" target="_blank" :to="{ name: '_assignmentDetail', params: { Id: row.Id } }">
                                    {{ row.jobTitle }}
                                </router-link>
                                <span v-else>{{ row.jobTitle }}</span>
                            </td>
                            <td>
                                <router-link v-if="row.companyId" target="_blank" :to="{ name: '_companyDetail', params: { Id: row.companyId } }">
                                    {{ row.company }}
                                    <span v-if="row.isKeyAccount"><i class="fal fa-key theme-key"></i></span>
                                    <span v-if="hasActivePPA(row)"><i class="fal fa-file-alt theme-ppa"></i></span>
                                </router-link>
                                <span v-else>
                                    {{ row.company }}
                                    <span v-if="row.isKeyAccount"><i class="fal fa-key theme-key"></i></span>
                                    <span v-if="hasActivePPA(row)"><i class="fal fa-file-alt theme-ppa"></i></span>
                                </span>
                            </td>
                            <td v-if="showHidden">
                                <router-link v-if="row.Id" target="_blank" :to="{ name: '_assignmentDetail', params: { Id: row.Id } }">
                                    {{ row.displayId }}
                                </router-link>
                                <span v-else>{{ row.displayId }}</span>
                            </td>
                            <td v-if="showHidden">{{ row.industry }}</td>
                            <td v-if="showHidden">{{ row.function }}</td>
                            <td v-if="showHidden">
                                <div v-for="license in Licenses(row)" :key="`${row.Id}-license-${license}`">
                                    {{ license }}
                                </div>
                            </td>
                            <td>
                                <div v-if="row.searchType == 'BLM' || row.searchType == 'Consulting'">
                                    {{ row.assignmentType }}
                                </div>
                                <div v-else>
                                    {{ $filters.SearchType(row.searchType) }}
                                </div>
                            </td>
                            <td>
                                <div class="partners">
                                    <div class="partner-line" v-for="partner in PartnerEntries(row)" :key="partner.key">
                                        <router-link
                                            v-if="partner.id"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            :to="{ name: '_partnerProfile', params: { Id: partner.id } }">
                                            {{ partner.name }}
                                        </router-link>
                                        <span v-else>{{ partner.name }}</span>
                                        <span> - {{ partner.percent }}</span>
                                    </div>
                                </div>
                            </td>
                            <td v-if="width > 58" style="white-space: nowrap">
                                <div v-for="user in ParseUsers(row)" :key="(user.ID || user.Id) + '-group-role'">
                                    <span style="white-space: nowrap">{{ $filters.UserRole(user) }}</span>
                                </div>
                            </td>
                            <td>
                                <div v-for="office in Offices(row)" :key="`${row.Id}-${office}`">
                                    {{ office }}
                                </div>
                            </td>
                            <td>{{ $filters.AssignmentStatus(row.status) }}</td>
                            <td>{{ moment(row.dateTaken).format("MM/DD/YYYY") }}</td>
                            <td class="has-text-right">
                                {{ $filters.Currency($store.getters.user.Currency, RowBillings(row)) }}
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
            </div>
        </div>
    </div>
</template>
<script>
import { ref, inject, computed, getCurrentInstance, watch } from "vue";

export default {
    props: {
        data: {
            type: Array,
            default: () => {
                return [];
            }
        },
        sort: {
            type: String,
            default: "dateTaken"
        },
        direction: {
            type: String,
            default: "desc"
        },
        width: {
            type: Number,
            default: null
        },
        perPage: {
            type: Number,
            default: 50
        },
        infiniteScroll: {
            type: Boolean,
            default: false
        },
        reportMode: {
            type: Boolean,
            default: false
        },
        groupBy: {
            type: String,
            default: "None"
        }
    },
    setup(props, context) {
        const moment = inject("$moment");

        const sort = ref(props.sort);
        const direction = ref(props.direction);

        const assignmentsLocal = computed(() => props.data);
        const filter = ref("");
        const showHidden = ref(false);

        const global = getCurrentInstance()?.appContext.config.globalProperties;

        const assignmentsFiltered = computed(() => {
            if (!filter.value || filter.value == "") return assignmentsLocal.value;

            const filterString = filter.value.toLowerCase();

            return assignmentsLocal.value.filter((x) => {
                if (
                    x.jobTitle?.toLowerCase().indexOf(filterString) >= 0 ||
                    x.company?.toLowerCase().indexOf(filterString) >= 0 ||
                    ParseUsers(x)?.some((u) => (u.FullName || u.fullName)?.toLowerCase().indexOf(filterString) >= 0) ||
                    global.$filters.AssignmentStatus(x.status)?.toLowerCase().indexOf(filterString) >= 0 ||
                    x.Id.toString().includes(filterString) ||
                    x.displayId.toString().includes(filterString)
                )
                    return true;
            });
        });

        const ParseUsers = (row) => {
            if (!row) return [];
            if (Array.isArray(row.__groupUsers)) return row.__groupUsers;
            if (row.__groupUser) return [row.__groupUser];
            if (Array.isArray(row._parsedUsers)) return row._parsedUsers;

            try {
                row._parsedUsers = JSON.parse(row.users || "[]") || [];
            } catch (err) {
                row._parsedUsers = [];
            }
            return row._parsedUsers;
        };

        const ParseReportGroups = (row) => {
            if (!row) return [];
            if (Array.isArray(row._parsedReportGroups)) return row._parsedReportGroups;

            try {
                row._parsedReportGroups = JSON.parse(row.reportGroups || "[]") || [];
            } catch (err) {
                row._parsedReportGroups = [];
            }
            return row._parsedReportGroups;
        };

        const UserPercent = (user) => {
            const value = user?.Percentage ?? user?.percentage;
            const parsed = Number(value);
            return Number.isFinite(parsed) ? parsed : 0;
        };

        const UserSplitBillings = (row, user) => {
            const take = Number(user?.Take ?? user?.take);
            if (Number.isFinite(take) && take > 0) return take;

            const totalBillings = Number(row?.totalBillings);
            if (!Number.isFinite(totalBillings)) return 0;

            return totalBillings * (UserPercent(user) / 100);
        };

        const PercentText = (value) => {
            const n = Number(value);
            if (!Number.isFinite(n)) return "0%";
            return `${Number(n.toFixed(2)).toString()}%`;
        };

        const PartnerEntries = (row) => {
            return ParseUsers(row).map((user) => ({
                key: `${user.ID || user.Id}-${user.LocationID || user.LocationId || ""}`,
                id: user.ID || user.Id,
                name: user.FullName || user.fullName,
                percent: PercentText(UserPercent(user))
            }));
        };

        const Offices = (row) => {
            const groupUserLocation = row?.__groupUser?.LocationName || row?.__groupUser?.locationName;
            if (groupUserLocation) return [groupUserLocation];

            const unique = new Set();
            ParseUsers(row).forEach((user) => {
                const name = user.LocationName || user.locationName;
                if (name) unique.add(name);
            });
            return [...unique];
        };

        const Licenses = (row) => {
            if (row?.__groupUser?.LicenseName) return [row.__groupUser.LicenseName];
            if (row?.__groupUser?.licenseName) return [row.__groupUser.licenseName];

            const unique = new Set();
            ParseUsers(row).forEach((user) => {
                const name = user.LicenseName || user.licenseName;
                if (name) unique.add(name);
            });
            return [...unique];
        };

        const groupedMode = computed(() => props.reportMode && props.groupBy !== "None");
        const groupedSortKey = ref(null);
        const groupedSortDirection = ref("none");
        const groupHeaderColspan = computed(() => {
            let count = 8;
            if (showHidden.value) count += 4;
            if (props.width > 58) count += 1;
            return count;
        });

        const SortValue = (row, sortKey) => {
            switch (sortKey) {
                case "jobTitle":
                    return (row.jobTitle || "").toString().toLowerCase();
                case "company":
                    return (row.company || "").toString().toLowerCase();
                case "displayId":
                    return (row.displayId || "").toString().toLowerCase();
                case "industry":
                    return (row.industry || "").toString().toLowerCase();
                case "function":
                    return (row.function || "").toString().toLowerCase();
                case "license":
                    return Licenses(row).join(", ").toLowerCase();
                case "searchType":
                    return (
                        (row.searchType == "BLM" || row.searchType == "Consulting"
                            ? row.assignmentType
                            : global.$filters.SearchType(row.searchType)) || ""
                    )
                        .toString()
                        .toLowerCase();
                case "partners":
                    return PartnerEntries(row)
                        .map((x) => x.name)
                        .join(", ")
                        .toLowerCase();
                case "role":
                    return ParseUsers(row)
                        .map((x) => global.$filters.UserRole(x))
                        .join(", ")
                        .toLowerCase();
                case "office":
                    return Offices(row).join(", ").toLowerCase();
                case "status":
                    return (global.$filters.AssignmentStatus(row.status) || "").toString().toLowerCase();
                case "dateTaken":
                    return moment(row.dateTaken).valueOf();
                case "billings":
                    return RowBillings(row);
                default:
                    return "";
            }
        };

        const SortRows = (rows) => {
            if (!groupedSortKey.value || groupedSortDirection.value === "none") {
                return [...rows];
            }

            const dir = groupedSortDirection.value === "asc" ? 1 : -1;
            return [...rows].sort((a, b) => {
                const av = SortValue(a, groupedSortKey.value);
                const bv = SortValue(b, groupedSortKey.value);
                if (typeof av === "number" || typeof bv === "number") {
                    return ((Number(av) || 0) - (Number(bv) || 0)) * dir;
                }
                return av.toString().localeCompare(bv.toString()) * dir;
            });
        };

        const groupedAssignments = computed(() => {
            if (!groupedMode.value) return [];

            const groups = {};

            const addGroupRow = (groupKey, row) => {
                const key = groupKey || "(None)";
                if (!groups[key]) groups[key] = [];
                groups[key].push(row);
            };

            const addGeographyGroupRow = (groupKey, row, user) => {
                const key = groupKey || "(None)";
                if (!groups[key]) groups[key] = [];

                const rowId = row.Id ?? row.id ?? row.displayId;
                let groupedRow = groups[key].find(
                    (x) => (x.Id ?? x.id ?? x.displayId) === rowId
                );

                if (!groupedRow) {
                    groupedRow = {
                        ...row,
                        __groupUsers: [],
                        __groupBillings: 0
                    };
                    groups[key].push(groupedRow);
                }

                groupedRow.__groupUsers.push(user);
                groupedRow.__groupBillings += UserSplitBillings(row, user);
            };

            assignmentsFiltered.value.forEach((row) => {
                const groupBy = props.groupBy;

                if (groupBy === "Company") {
                    addGroupRow(row.company, row);
                } else if (groupBy === "Parent") {
                    addGroupRow(row.parent, row);
                } else if (groupBy === "Function") {
                    addGroupRow(row.function, row);
                } else if (groupBy === "Industry") {
                    addGroupRow(row.industry, row);
                } else if (groupBy === "Status") {
                    addGroupRow(global.$filters.AssignmentStatus(row.status), row);
                } else if (groupBy === "Report Group") {
                    const asBool = (value) =>
                        value === true || value === "true" || value === 1 || value === "1";

                    const groupsForAssignment = ParseReportGroups(row).filter((x) => {
                        const type = (x?.Type || x?.type || "").toString().toLowerCase();
                        if (type) return type === "reportgroup";

                        const practiceGroup = x?.PracticeGroup ?? x?.practiceGroup;
                        const subsector = x?.Subsector ?? x?.subsector;

                        // Legacy shape: report groups are neither PracticeGroup nor Subsector.
                        if (practiceGroup !== undefined || subsector !== undefined) {
                            return !asBool(practiceGroup) && !asBool(subsector);
                        }

                        return false;
                    });

                    if (groupsForAssignment.length === 0) {
                        addGroupRow("(None)", row);
                    } else {
                        const groupNames = [...new Set(groupsForAssignment.map((g) => g.Name || g.name))];
                        groupNames.forEach((name) => addGroupRow(name, row));
                    }
                } else if (groupBy === "Partner") {
                    ParseUsers(row).forEach((user) => {
                        const key = user.FullName || user.fullName;

                        const clone = {
                            ...row,
                            __groupUser: user,
                            __groupBillings: UserSplitBillings(row, user)
                        };
                        addGroupRow(key, clone);
                    });
                } else if (groupBy === "Location" || groupBy === "License" || groupBy === "Region") {
                    ParseUsers(row).forEach((user) => {
                        const key =
                            groupBy === "Location"
                                ? user.LocationName || user.locationName
                                : groupBy === "License"
                                  ? user.LicenseName || user.licenseName
                                  : user.RegionName || user.regionName;

                        addGeographyGroupRow(key, row, user);
                    });
                } else {
                    addGroupRow("(None)", row);
                }
            });

            return Object.keys(groups)
                .sort((a, b) => a.localeCompare(b))
                .map((key) => ({ key, rows: SortRows(groups[key]) }));
        });

        const collapsedGroups = ref({});

        watch(
            groupedAssignments,
            (groups) => {
                const nextState = {};
                groups.forEach((group) => {
                    nextState[group.key] = collapsedGroups.value[group.key] === true;
                });
                collapsedGroups.value = nextState;
            },
            { immediate: true }
        );

        const ToggleGroup = (groupKey) => {
            collapsedGroups.value = {
                ...collapsedGroups.value,
                [groupKey]: !collapsedGroups.value[groupKey]
            };
        };

        const GroupCollapsed = (groupKey) => collapsedGroups.value[groupKey] === true;
        const CollapseAllGroups = () => {
            const next = {};
            groupedAssignments.value.forEach((group) => {
                next[group.key] = true;
            });
            collapsedGroups.value = next;
        };
        const ExpandAllGroups = () => {
            const next = {};
            groupedAssignments.value.forEach((group) => {
                next[group.key] = false;
            });
            collapsedGroups.value = next;
        };
        const ToggleGroupedSort = (key) => {
            if (groupedSortKey.value === key) {
                if (groupedSortDirection.value === "asc") {
                    groupedSortDirection.value = "desc";
                    return;
                }

                if (groupedSortDirection.value === "desc") {
                    groupedSortDirection.value = "none";
                    groupedSortKey.value = null;
                    return;
                }

                groupedSortDirection.value = "asc";
                return;
            }

            groupedSortKey.value = key;
            groupedSortDirection.value = "asc";
        };
        const SortState = (key) => {
            if (groupedSortKey.value !== key) return "none";
            return groupedSortDirection.value === "asc" ? "asc" : "desc";
        };

        const consultingType = computed(() => {
            return assignmentsLocal.value.assignmentType.replace("Leadership Consulting - ", "");
        });

        const openColumns = ref(props.perPage);

        const hasActivePPA = (row) => row?.hasActivePPA || row?.HasActivePPA || row?.hasActivePpa;
        const RowBillings = (row) => Number(row?.__groupBillings ?? row?.totalBillings ?? 0);

        if (props.infiniteScroll) {
            const main = document.getElementById("main");
            main.onscroll = function (event) {
                const element = event.target;

                if (element.scrollHeight - element.scrollTop <= element.clientHeight + 2) {
                    openColumns.value += props.perPage;
                }
            };
        }

        return {
            moment,
            filter,
            showHidden,
            assignmentsFiltered,
            groupedMode,
            groupedAssignments,
            openColumns,
            consultingType,
            hasActivePPA,
            ParseUsers,
            PartnerEntries,
            Offices,
            Licenses,
            RowBillings,
            ToggleGroup,
            GroupCollapsed,
            CollapseAllGroups,
            ExpandAllGroups,
            groupHeaderColspan,
            ToggleGroupedSort,
            SortState
        };
    }
};
</script>
<style lang="scss">
@use "@/assets/css/globalVars.scss" as *;

.assignment-table-wrap {
    &>.b-table.is-mobile>.table-wrapper>.table>tbody>tr>td {
        &:first-child {
            border-bottom: none !important;
            padding-bottom: 0 !important;
            //background-color: transparent;
        }

        //To be revised
        &:nth-child(2) {
            border-top: none;
            border-bottom: 2px solid #cacaca !important;
            padding-top: 0;

            background-color: #f6f6f6;

            &::before {
                content: "";
                display: none;
            }

            & a {
                color: $text-gray;
                font-size: pxUnits(18);

                &:hover {
                    color: $boyden-blue;
                }
            }
        }
    }
}
</style>
<style lang="scss" scoped>
.partners {
    display: flex;
    flex-direction: column;
}

.partner-line {
    white-space: nowrap;
}

.group-controls {
    display: flex;
    width: fit-content;
    gap: 0.5rem;
}

.grouped-assignment-results {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.grouped-assignment-scroll {
    overflow-x: auto;
    overflow-y: hidden;
}

.assignment-group-table {
    border: 1px solid #ddd;
    min-width: 1200px;
}

.is-sortable {
    cursor: pointer;
    user-select: none;
    color: #3d3d3d;
    font-weight: 700;
    white-space: nowrap;
}

.assignment-group-table thead th {
    vertical-align: middle;
    padding: 0.75rem 0.65rem;
}

.sortable-label {
    display: inline-flex;
    align-items: center;
    gap: 0.32rem;
    line-height: 1.1;
}

.sort-indicator {
    display: inline-block;
    width: 0;
    height: 0;
}

.sort-indicator.none {
    border: 0;
}

.sort-indicator.desc {
    border-left: 0.35rem solid transparent;
    border-right: 0.35rem solid transparent;
    border-top: 0.6rem solid #2f2f2f;
}

.sort-indicator.asc {
    border-left: 0.35rem solid transparent;
    border-right: 0.35rem solid transparent;
    border-bottom: 0.6rem solid #2f2f2f;
}

.assignment-group-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: 600;
}

.assignment-group-summary {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
}

.assignment-group-count {
    color: #666;
}

.assignment-group-toggle {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: transparent;
    border: 0;
    padding: 0;
    font: inherit;
    color: inherit;
    cursor: pointer;
}

.assignment-group-chevron {
    transition: transform 0.2s ease;
}

.assignment-group-chevron.collapsed {
    transform: rotate(-90deg);
}

.assignment-group-header-row td {
    background: #e5e5e5;
    border-top: 1px solid #cfcfcf;
    border-bottom: 1px solid #cfcfcf;
}
</style>
